// @ts-strict-ignore
import { Box3, LineSegments, Material, Mesh, MeshBasicMaterial, Object3D, Vector3 } from 'three';
import { SimpleTextValueVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { ILayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { SceneManager } from '../../manager/scene.manager';
import { ConfiguratorParameters } from '../../configurator-parameters/configurator-parameters';
import { GeometryUtils } from '../../common/util/geometry-utils';

export class DimensionElement extends Object3D implements ILayerElement {
  public static readonly DEFAULT_FONT_SIZE: number = 12;

  public static readonly LINE_COLOR: number = 0x000000;
  public static readonly LINE_THICKNESS: number = 0.8;
  public static readonly LEVEL_OFFSET: number = 70;
  public static readonly ARROW_LENGTH: number = 20;
  public static readonly ARROW_ANGLE: number = 15;
  public static readonly LINE_MULTIPLIER: number = 0.9;
  public static readonly ARROW_MULTIPLIER: number = 0.7;
  public static readonly OUTSIDE_ARROW_LINE_LENGTH: number = 45;
  public static readonly LABEL_OFFSET: number = 20;
  public static readonly OFFSET: number = 35;
  protected static readonly LABEL_VERTICAL_SPACING: number = 5;
  protected static readonly LABEL_WIDTH: number = 60;

  private static BACKGROUND_PADDING: number = 6;
  private static BACKGROUND_Y_OFFSET: number = 0.1;
  private static BACKGROUND_Z_OFFSET: number = 3;

  public layerElementType: string = 'ILayerElement';

  public arrowInside: boolean = true;

  protected points: Vector3[];
  protected material: Material;
  protected topPointLeft: Vector3;
  protected topPointRight: Vector3;
  protected bottomPointLeft: Vector3;
  protected bottomPointRight: Vector3;

  protected lines: Object3D;
  protected leftLine: LineSegments;
  protected rightLine: LineSegments;

  protected arrowLines: Object3D;
  protected arrowPointLeft: Vector3;
  protected arrowPointRight: Vector3;
  protected text: TextValueVO;
  protected textWidth: number;
  protected textHeight: number;
  protected levelOffset: number;
  protected fontSize: number;
  protected fontScale: number;
  protected arrowMultiplier: number;

  private _createBackground: boolean = false;

  constructor(protected sceneManager: SceneManager, points: Vector3[], text: TextValueVO, material: Material, level: number = 0,
    levelOffset: number = DimensionElement.LEVEL_OFFSET, fontSize: number = NaN, fontScale: number = NaN,
    arrowMultiplier: number = DimensionElement.ARROW_MULTIPLIER, createBackground: boolean = false, initAfterCreate: boolean = true) {
    super();
    this.points = points;
    this.text = text;
    this.material = material;
    this.levelOffset = level * DimensionElement.LEVEL_OFFSET;
    this.fontSize = isNaN(fontSize) ? DimensionElement.DEFAULT_FONT_SIZE : fontSize;
    this.fontScale = isNaN(fontScale) ? ConfiguratorParameters.labeling.defaultFontScale : fontScale;
    this.arrowMultiplier = arrowMultiplier;
    this._createBackground = createBackground;

    if (initAfterCreate) {
      this.init();
    }
  }

  protected init(): void {
    this.calculatePoints();
    this.createLines();
    this.createArrowLine();
    this.createText(this.text);
  }

  protected calculatePoints(): void {
    this.textWidth = DimensionUtils.getTextSize((this.text as SimpleTextValueVO).text, this.fontSize, this.fontScale).x;
    this.textHeight = DimensionUtils.getTextSize((this.text as SimpleTextValueVO).text, this.fontSize, this.fontScale).y;

    this.bottomPointLeft = this.points[0];
    this.bottomPointRight = this.points[3];

    this.topPointLeft = MathUtils.getInterpolated(this.points[1], this.bottomPointLeft, DimensionElement.LINE_MULTIPLIER);
    this.topPointRight = MathUtils.getInterpolated(this.points[2], this.bottomPointRight, DimensionElement.LINE_MULTIPLIER);
    this.topPointLeft.z += this.levelOffset;
    this.topPointRight.z += this.levelOffset;

    this.arrowPointLeft = MathUtils.getInterpolated(this.points[1], this.bottomPointLeft, this.arrowMultiplier);
    this.arrowPointRight = MathUtils.getInterpolated(this.points[2], this.bottomPointRight, this.arrowMultiplier);
    this.arrowPointLeft.z += this.levelOffset;
    this.arrowPointRight.z += this.levelOffset;
  }

  protected createLines(): void {
    this.lines = new Object3D();
    this.leftLine = DimensionUtils.createLineSegment(this.points[0], this.topPointLeft, DimensionElement.LINE_COLOR);
    this.lines.add(this.leftLine);
    this.rightLine = DimensionUtils.createLineSegment(this.points[3], this.topPointRight, DimensionElement.LINE_COLOR);
    this.lines.add(this.rightLine);
    this.add(this.lines);
    if (this._createBackground) {
      this.createLineBackground(this.leftLine);
      this.createLineBackground(this.rightLine);
    }
  }

  protected createArrowLine(): void {
    this.arrowLines = new Object3D();

    this.arrowInside = !DimensionUtils.isDimensionNeedsOuterLabel(this.topPointLeft, this.topPointRight, this.textWidth);

    this.createArrow();

    if (!this.arrowInside) {
      this.createOutsideArrowLines();
    }

    const line: LineSegments = DimensionUtils.createLineSegment(this.arrowPointLeft, this.arrowPointRight, DimensionElement.LINE_COLOR);
    this.arrowLines.add(line);
    this.add(this.arrowLines);
    if (this._createBackground) {
      this.createLineBackground(line);
    }
  }

  protected createArrow(): void {
    const arrowLength: number = DimensionElement.ARROW_LENGTH * (this.arrowInside ? 1 : -1);
    const arrowRadius: number = arrowLength * Math.tan(DimensionElement.ARROW_ANGLE * MathUtils.TO_RADIANS * .5);

    const p1Left: Vector3 = new Vector3(this.arrowPointLeft.x - arrowLength, this.arrowPointLeft.y, this.arrowPointLeft.z + arrowRadius);
    const p2Left: Vector3 = new Vector3(this.arrowPointLeft.x - arrowLength, this.arrowPointLeft.y, this.arrowPointLeft.z - arrowRadius);
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointLeft, p1Left, DimensionElement.LINE_COLOR));
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointLeft, p2Left, DimensionElement.LINE_COLOR));
    // this face is necessary to fill the space between lines
    const leftArrowMesh: Mesh = FaceUtils.createSingleFaceMesh(p1Left, p2Left, this.arrowPointLeft, this.material);
    this.add(leftArrowMesh);

    const p1Right: Vector3 = new Vector3(this.arrowPointRight.x + arrowLength, this.arrowPointRight.y,
      this.arrowPointRight.z + arrowRadius);
    const p2Right: Vector3 = new Vector3(this.arrowPointRight.x + arrowLength, this.arrowPointRight.y,
      this.arrowPointRight.z - arrowRadius);
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointRight, p1Right, DimensionElement.LINE_COLOR));
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointRight, p2Right, DimensionElement.LINE_COLOR));
    // this face is necessary to fill the space between lines
    const rightArrowMesh: Mesh = FaceUtils.createSingleFaceMesh(p2Right, p1Right, this.arrowPointRight, this.material);
    this.add(rightArrowMesh);

    if (this._createBackground) {
      const arrowMeshHeight: number = Math.abs(arrowRadius) * 2;
      this.createArrowBackground(leftArrowMesh, arrowMeshHeight);
      this.createArrowBackground(rightArrowMesh, arrowMeshHeight);
    }
  }

  private createArrowBackground(arrowMesh: Mesh, arrowHeight: number): void {
    const bbox: Box3 = new Box3().setFromObject(arrowMesh);
    const rightArrowConnectPosition: Vector3 = new Vector3((bbox.min.x + bbox.max.x) / 2,
      bbox.min.y - DimensionElement.BACKGROUND_Y_OFFSET, (bbox.min.z + bbox.max.z) / 2);
    const backgroundLabel: Mesh = DimensionUtils.createBackground(DimensionElement.ARROW_LENGTH + DimensionElement.BACKGROUND_PADDING,
      arrowHeight + DimensionElement.BACKGROUND_PADDING, this.getWhiteMaterial(), rightArrowConnectPosition);
    this.add(backgroundLabel);
  }

  protected createOutsideArrowLines(): void {
    const arrowBaselineLeftEnd: Vector3 = this.arrowPointLeft.clone().add(
      new Vector3(this.textWidth + DimensionElement.OUTSIDE_ARROW_LINE_LENGTH));
    const leftLine: LineSegments = DimensionUtils.createLineSegment(this.arrowPointLeft, arrowBaselineLeftEnd, DimensionElement.LINE_COLOR);
    this.arrowLines.add(leftLine);

    const arrowBaselineRightEnd: Vector3 = this.arrowPointRight.clone().sub(new Vector3(DimensionElement.OUTSIDE_ARROW_LINE_LENGTH));
    const rightLine: LineSegments = DimensionUtils.createLineSegment(this.arrowPointRight, arrowBaselineRightEnd, DimensionElement.LINE_COLOR);
    this.arrowLines.add(rightLine);

    if (this._createBackground) {
      this.createLineBackground(leftLine);
      this.createLineBackground(rightLine);
    }
  }

  protected createText(text: TextValueVO): void {
    const label: LabelElement = new LabelElement(text, this.fontSize, LabelElement.DEFAULT_ROTATION,
      LabelElement.DEFAULT_LABEL_LENGTH, DimensionElement.LABEL_WIDTH, this.fontScale);
    const labelPosition: Vector3 = this.getLabelPosition();
    label.position.set(labelPosition.x, labelPosition.y, labelPosition.z);
    this.add(label);
    if (this._createBackground) {
      const backgroundPosition: Vector3 = label.position.clone().sub(new Vector3(0, DimensionElement.BACKGROUND_Y_OFFSET,
        -DimensionElement.BACKGROUND_Z_OFFSET));
      const backgroundLabel: Mesh = DimensionUtils.createBackground(this.textWidth + DimensionElement.BACKGROUND_PADDING,
        this.textHeight - DimensionElement.LABEL_VERTICAL_SPACING, this.getWhiteMaterial(), backgroundPosition);
      this.add(backgroundLabel);
    }
  }

  protected getLabelPosition(): Vector3 {
    const labelZPosition: number = this.getLabelZPosition();
    return this.arrowInside
      ? new Vector3((this.arrowPointLeft.x + this.arrowPointRight.x) / 2,
        (this.arrowPointLeft.y + this.arrowPointRight.y) / 2, labelZPosition)
      : new Vector3(this.arrowPointLeft.x + this.textWidth / 2 + DimensionElement.OFFSET,
        (this.arrowPointLeft.y + this.arrowPointRight.y) / 2, labelZPosition);
  }

  protected getLabelZPosition(): number {
    return this.arrowPointLeft.z + (this.textHeight - DimensionElement.LABEL_VERTICAL_SPACING) / 2;
  }

  private createLineBackground(line: LineSegments): void {
    const vertices = GeometryUtils.getVertices(line.geometry);
    const start: Vector3 = vertices[0];
    const end: Vector3 = vertices[1];
    const arrowLineConnectPosition: Vector3 = new Vector3((start.x + end.x) / 2, start.y - DimensionElement.BACKGROUND_Y_OFFSET,
      (start.z + end.z) / 2);
    const width: number = start.distanceTo(end)
      + DimensionElement.BACKGROUND_PADDING;
    const height: number = DimensionElement.LINE_THICKNESS + DimensionElement.BACKGROUND_PADDING;
    let backgroundMesh: Mesh;
    if (MathUtils.compare(start.z, end.z) === 0) {
      backgroundMesh = DimensionUtils.createBackground(width, height, this.getWhiteMaterial(), arrowLineConnectPosition);
    } else {
      backgroundMesh = DimensionUtils.createBackground(height, width, this.getWhiteMaterial(), arrowLineConnectPosition);
    }

    this.add(backgroundMesh);
  }

  private getWhiteMaterial(): MeshBasicMaterial {
    return new MeshBasicMaterial({color: 0xFFFFFF});
  }
}
